/* eslint-disable complexity */
import './OfferPhoneModal2.css';

import React from 'react';

import classname from '@search/classname/src';
import { MortgagePurpose } from '@search/graphql-typings';
import { Modal, Input, Button } from '@vtblife/uikit';
import Icon from '@vtblife/uikit-icons';

import { useUserEmailContext } from '../../../../domain/emails/useUserEmailContext';
import { ImageLazySeo } from '../../../../domain/image/lazy/ImageLazySeo';
import { useClassifiedOfferDataLayerPush } from '../../../../domain/offer/analytics/ClassifiedOfferGtmContext';
import { OfferType, useOfferQuery } from '../../../../domain/offer/search/page/OfferQuery';
import { OfferSnippetModel } from '../../../../domain/offer/search/page/SearchOffersQuery';
import { ImageLazySrcSetProvide, useClImageLazySrcSetUrl } from '../../../../domain/image/lazy/ImageLazySrcSet';
import { useMedia } from '../../hooks/useMedia';
import { useOfferPhoneModal2Mutation } from './OfferPhoneModal2Mutation';

import { OfferPhoneModalMobile } from './OfferPhoneModalMobile';
import { OfferPhoneModalDesktop } from './OfferPhoneModalDesktop';

const cn = classname.bind(null, 'OfferPhoneModal2');

type Props = {
    isNewBuilding?: boolean;
    isSellFlat?: boolean;
    offerId?: string;
    phoneHref: string;
    phoneFormatted: string;
    isDoppelganger: boolean;
    onClose(): void;
    offer?: OfferType;
    snippet?: OfferSnippetModel;
    mortgagePurpose?: MortgagePurpose;
    placeCall?: 'check_request_link' | 'check_request_clop';
};

export function OfferPhoneModal2(p: Props) {
    const isMobile = useMedia('(max-width: 768px)');

    // RS-6980: решили откатить юридическую проверку из расхлопа
    // if (! p.isNewBuilding && p.isSellFlat) return (<OfferPhoneModal2New {...p} />);

    if (isMobile) return (<OfferPhoneModalMobile {...p} />);

    return (<OfferPhoneModalDesktop {...p} />);
}

export const emailRegexp = new RegExp(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i);

function OfferPhoneModal2New({
    offer: offerCard,
    snippet,
    offerId,
    phoneFormatted,
    phoneHref,
    isDoppelganger,
    onClose,
    placeCall
}: Props) {
    const loadOffer = useOfferQuery();

    const response = offerId && ! offerCard && ! snippet ? loadOffer({
        offerId,
        skipNotMain: true
    }) : undefined;

    const offer = (
        response?.data?.offer.__typename === 'ProfOfferSnippet' ? response.data.offer : undefined
    ) ?? offerCard ?? snippet;

    const dataLayerPush = useClassifiedOfferDataLayerPush();
    const { primaryEmail } = useUserEmailContext();
    const [ flatNumber, setFlatNumber ] = React.useState('');
    const [ email, setEmail ] = React.useState(primaryEmail ?? '');
    const offerPhoneModeal2Mutation = useOfferPhoneModal2Mutation();
    const [ isTouched, setIsTouched ] = React.useState({ flatNumber: false, email: false });
    const enableTouched = React.useCallback((field?: keyof typeof isTouched) => setIsTouched(
        prev => field ? { ...prev, [field]: true } : { flatNumber: true, email: true }
    ), []);
    const isFlatNumberError = ! flatNumber.trim();
    const isEmailError = ! emailRegexp.test(email);
    const [ saving, setSaving ] = React.useState<boolean | string>();

    const validateFlat = React.useCallback(async () => {
        enableTouched();
        if (isFlatNumberError || isEmailError) return;
        if (! offer) return;

        try {
            setSaving(true);
            const res = await offerPhoneModeal2Mutation({
                subject: 'Classified, проверка оффера',
                body: `Ссылка: ${window.location.href
                }\nАдрес: ${offer.location.formattedAddressLong
                }\nКвартира №: ${flatNumber
                }\nEmail: ${email}`
            });

            if (res.data?.createEmailCallbackProverka.emailMessageId && placeCall) {
                dataLayerPush({
                    event: 'up_action',
                    theme: 'legal',
                    type: placeCall
                });
            }

            setSaving(res?.errors?.[0].message ?? false);
        } catch (e: any) {
            setSaving(e?.message || 'Что-то пошло не так');
        }
    }, [ isFlatNumberError, isEmailError, enableTouched, dataLayerPush, flatNumber, email, offerPhoneModeal2Mutation, offer ]);

    const isMobile = useMedia('(max-width: 768px)');

    const getUrl = useClImageLazySrcSetUrl({ baseUrl: offer?.gallery?.baseUrl });

    if (saving === false) {
        return (
            <Modal mobile={isMobile} opened size='s' onClose={onClose}>
                {isMobile && (
                    <Modal.Header>
                        <div className={cn('title2')}>Заявка принята</div>
                    </Modal.Header>
                )}
                { ! isMobile && (
                    <Modal.Title>
                        <div className={cn('title2')}>Заявка принята</div>
                    </Modal.Title>
                )}
                <div className={cn('close-content')}>
                    После проверки квартиры мы пришлём вам отчет на почту {email}
                    <Button
                        fullWidth
                        dataTest='validate-close'
                        variant='secondary'
                        onClick={onClose}
                    >Закрыть</Button>
                </div>
            </Modal>
        );
    }

    return (
        <Modal opened size={isMobile ? 's' : 'm'} onClose={onClose}>
            <Modal.Content>
                <div className={cn()}>
                    <div className={cn('rotate')}>
                        <div className={cn('header')}>
                            <div className={cn('phone')}>
                                <a href={phoneHref}>{phoneFormatted}</a>
                                {isDoppelganger && (<Icon size='l' name='shield-check' color='#78BC29' />)}
                            </div>
                        </div>
                        <div className={cn('main')}>
                            <div className={cn('title')}>
                                Защититесь от&nbsp;мошенников
                            </div>
                            <div className={cn('main-text')}>
                                Бесплатно проверим данные о&nbsp;квартире и&nbsp;пришлём отчёт о&nbsp;собственниках, арестах и&nbsp;других ограничениях.
                            </div>
                        </div>
                        <div className={cn('offer')}>
                            {! offer?.gallery?.mainImage.originPath ? (
                                <div className={cn('offer-img')}>
                                    <Icon name='camera-crossed' size='m' />
                                </div>
                            ) : (
                                <ImageLazySrcSetProvide getUrl={getUrl}>
                                    <ImageLazySeo
                                        originPath={offer.gallery.mainImage.originPath}
                                        srcWidth={72}
                                        srcHeight={72}
                                        className={cn('offer-img')}
                                    />
                                </ImageLazySrcSetProvide>
                            )}
                            <div className={cn('offer-description')}>
                                <div>{offer?.location.formattedAddressLong}</div>
                                <div className={cn('disclamer')}>
                                    <Icon size='s' name='exclamation-circle' color='hsla(251, 15%, 50%, 0.5)' />
                                    Уточните у&nbsp;продавца номер квартиры
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={cn('controls')}>
                        <Input
                            dataTest='flat-number'
                            hint='узнайте у продавца'
                            disabled={saving === true}
                            onBlur={enableTouched.bind(null, 'flatNumber')}
                            onChange={setFlatNumber}
                            placeholder='Номер квартиры'
                            required
                            size='m'
                            error={isTouched.flatNumber && isFlatNumberError}
                            value={flatNumber}
                        />
                        <Input
                            dataTest='email'
                            inputMode='email'
                            type='email'
                            disabled={saving === true}
                            hint={saving && saving !== true ? saving : 'сюда пришлём отчёт'}
                            onBlur={enableTouched.bind(null, 'email')}
                            onChange={setEmail}
                            placeholder='Ваш email'
                            required
                            size='m'
                            error={(isTouched.email && isEmailError) || Boolean(saving && saving !== true)}
                            value={email}
                        />
                    </div>
                    <Button
                        fullWidth
                        loading={saving === true}
                        dataTest='validate-flat'
                        variant='brand-turquoise'
                        onClick={validateFlat}
                    >Проверить объект</Button>
                    <div className={cn('warn')}>
                        Проверьте корректность адреса перед отправкой.
                        В случае ошибки, мы{' '}не{' '}сможем провести проверку.
                    </div>
                </div>
            </Modal.Content>
        </Modal>
    );
}
